import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';

/**
 * Time-based accent themes
 * Changes throughout the day for visual variety
 */
const TIME_ACCENTS = {
  dawn: {
    name: 'Aube',
    hours: [5, 6, 7],
    accent: '#f59e0b',
    accentGlow: 'rgba(245, 158, 11, 0.15)',
  },
  morning: {
    name: 'Matin',
    hours: [8, 9, 10, 11],
    accent: '#3b82f6',
    accentGlow: 'rgba(59, 130, 246, 0.15)',
  },
  afternoon: {
    name: 'Après-midi',
    hours: [12, 13, 14, 15, 16, 17],
    accent: '#22c55e',
    accentGlow: 'rgba(34, 197, 94, 0.15)',
  },
  evening: {
    name: 'Soir',
    hours: [18, 19, 20, 21],
    accent: '#a855f7',
    accentGlow: 'rgba(168, 85, 247, 0.15)',
  },
  night: {
    name: 'Nuit',
    hours: [22, 23, 0, 1, 2, 3, 4],
    accent: '#6366f1',
    accentGlow: 'rgba(99, 102, 241, 0.15)',
  },
};

/**
 * Color schemes for light and dark modes
 */
const COLOR_SCHEMES = {
  dark: {
    bgBase: '#08080c',
    bgSurface: '#0e0e14',
    bgElevated: '#14141c',
    bgCard: '#181820',
    bgHover: '#1e1e28',
    textPrimary: '#fafafa',
    textSecondary: '#a1a1aa',
    textTertiary: '#71717a',
    textMuted: '#52525b',
    borderSubtle: 'rgba(255, 255, 255, 0.06)',
    borderDefault: 'rgba(255, 255, 255, 0.1)',
    borderStrong: 'rgba(255, 255, 255, 0.16)',
  },
  light: {
    // Warm off-white palette — easy on the eyes, inspired by Arc / Bear / Apple Notes
    bgBase:    '#F3EDE3',   // warm parchment — no glare
    bgSurface: '#FAF6EF',   // warm paper white
    bgElevated:'#EDE6D9',   // warm light gray
    bgCard:    '#FEFAF4',   // near-white with warmth
    bgHover:   '#E6DFD0',   // warm hover state

    textPrimary:   '#292118',  // warm near-black (softer than pure #000)
    textSecondary: '#6B5D4F',  // warm brown-gray
    textTertiary:  '#9E8E7E',  // warm medium gray
    textMuted:     '#C0B0A0',  // warm muted

    borderSubtle: 'rgba(105, 82, 55, 0.08)',
    borderDefault:'rgba(105, 82, 55, 0.13)',
    borderStrong: 'rgba(105, 82, 55, 0.22)',
  },
};

const STORAGE_KEY = 'adhd-planner-theme-mode';

const getTimeAccent = (hour) => {
  for (const [key, accent] of Object.entries(TIME_ACCENTS)) {
    if (accent.hours.includes(hour)) {
      return { key, ...accent };
    }
  }
  return { key: 'morning', ...TIME_ACCENTS.morning };
};

const getSystemPreference = () => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'dark';
};

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  // Mode: 'dark' | 'light' | 'system'
  const [mode, setMode] = useState(() => {
    if (typeof localStorage !== 'undefined') {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && ['dark', 'light', 'system'].includes(saved)) {
        return saved;
      }
    }
    return 'light'; // Default to light mode
  });

  const [systemPreference, setSystemPreference] = useState(getSystemPreference);
  const [currentHour, setCurrentHour] = useState(() => new Date().getHours());

  // Listen for system preference changes
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => setSystemPreference(e.matches ? 'dark' : 'light');

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Update hour periodically
  useEffect(() => {
    const interval = setInterval(() => {
      const hour = new Date().getHours();
      if (hour !== currentHour) {
        setCurrentHour(hour);
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [currentHour]);

  // Persist mode preference
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  // Computed values
  const resolvedMode = mode === 'system' ? systemPreference : mode;
  const isDark = resolvedMode === 'dark';
  const timeAccent = useMemo(() => getTimeAccent(currentHour), [currentHour]);
  const colors = COLOR_SCHEMES[resolvedMode];

  // Apply CSS variables and class to document
  useEffect(() => {
    const root = document.documentElement;

    // Toggle dark class for Tailwind
    if (isDark) {
      root.classList.add('dark');
      root.classList.remove('light');
    } else {
      root.classList.add('light');
      root.classList.remove('dark');
    }

    // Apply color scheme CSS variables
    root.style.setProperty('--bg-base', colors.bgBase);
    root.style.setProperty('--bg-surface', colors.bgSurface);
    root.style.setProperty('--bg-elevated', colors.bgElevated);
    root.style.setProperty('--bg-card', colors.bgCard);
    root.style.setProperty('--bg-hover', colors.bgHover);

    root.style.setProperty('--text-primary', colors.textPrimary);
    root.style.setProperty('--text-secondary', colors.textSecondary);
    root.style.setProperty('--text-tertiary', colors.textTertiary);
    root.style.setProperty('--text-muted', colors.textMuted);

    root.style.setProperty('--border-subtle', colors.borderSubtle);
    root.style.setProperty('--border-default', colors.borderDefault);
    root.style.setProperty('--border-strong', colors.borderStrong);

    // Apply time-based accent
    root.style.setProperty('--theme-accent', timeAccent.accent);
    root.style.setProperty('--theme-accent-glow', timeAccent.accentGlow);

    // Legacy compatibility
    root.style.setProperty('--theme-bg-base', colors.bgBase);
    root.style.setProperty('--theme-bg-surface', colors.bgSurface);
    root.style.setProperty('--theme-bg-elevated', colors.bgElevated);
    root.style.setProperty('--theme-bg-card', colors.bgCard);
  }, [isDark, colors, timeAccent]);

  // Actions
  const toggleMode = useCallback(() => {
    setMode((current) => (current === 'dark' ? 'light' : 'dark'));
  }, []);

  const setDark = useCallback(() => setMode('dark'), []);
  const setLight = useCallback(() => setMode('light'), []);
  const setSystem = useCallback(() => setMode('system'), []);

  const value = useMemo(
    () => ({
      // State
      mode,
      resolvedMode,
      isDark,
      isLight: !isDark,
      colors,

      // Time-based accent
      timeAccent,
      currentHour,
      isNight: timeAccent.key === 'night',
      isDawn: timeAccent.key === 'dawn',
      isMorning: timeAccent.key === 'morning',
      isAfternoon: timeAccent.key === 'afternoon',
      isEvening: timeAccent.key === 'evening',

      // Legacy compatibility
      theme: {
        key: timeAccent.key,
        name: timeAccent.name,
        colors: {
          ...colors,
          accent: timeAccent.accent,
          accentGlow: timeAccent.accentGlow,
        },
      },

      // Actions
      toggleMode,
      setMode,
      setDark,
      setLight,
      setSystem,
    }),
    [mode, resolvedMode, isDark, colors, timeAccent, currentHour, toggleMode, setDark, setLight, setSystem]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export { TIME_ACCENTS, COLOR_SCHEMES };
